import { Button, Card, Skeleton, Space, Table, Typography } from "antd"
import { useState } from "react"
import { EstimateStatuses, projectTypeName } from "../../../common/constants"
import { centsToDollarsDisplay } from "../../displayFormatters"
import { getProjectUrl } from "../datacenterModel"
import { EstimateReportData, EstimatesSummary } from "./estimatesReportApi"
import { Proposal } from "../../jobs/Proposals/proposalsModel"
import ProposalTimeline from "../../jobs/Proposals/ProposalTimeline"
import { useGetJobProposalsQuery } from "../ProposalsReport/proposalsReportQueries"
import dayjs from "dayjs"
import { useSavedState } from '../../../hooks/useSavedState'
import { Division } from "../../../domain/divisions/division"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPaperPlane, faPaperPlane as faPaperPlaneDuoTone, faThumbsUp, faXmarkToSlot } from "@fortawesome/pro-duotone-svg-icons"
import { grey, blue, green, red } from "@ant-design/colors"

const { Text } = Typography

type FiltersListItem = {
  id: string
  value: string
}

const filtersList: FiltersListItem[] = [
  { id: 'all', value: 'All' },
  { id: 'unsent', value: 'Drafts' },
  { id: 'sent', value: 'Sent' },
  { id: 'approved', value: 'Approved' },
  { id: 'lost', value: 'Lost' }
]

const estimateStatusIcons = {
  [EstimateStatuses.Unsent]: <FontAwesomeIcon icon={faPaperPlane} color={grey[0]} size="lg" />,
  [EstimateStatuses.Sent]: <FontAwesomeIcon icon={faPaperPlaneDuoTone} color={blue.primary} size="lg" />,
  [EstimateStatuses.Approved]: <FontAwesomeIcon icon={faThumbsUp} color={green.primary} size="lg" />,
  [EstimateStatuses.Lost]: <FontAwesomeIcon icon={faXmarkToSlot} color={red.primary} size="lg" />
}

type Props = {
  estimates: EstimatesSummary
  divisions: Division[]
}

const EstimatesReportJobList = ({ estimates, divisions }: Props) => {
  const [activeFilter, setActiveFilter] = useSavedState<string>('estimates-report-active-filter', 'all');
  const [expandedJobId, setExpandedJobId] = useState<number[]>([])
  const proposalsQuery = useGetJobProposalsQuery(expandedJobId.length > 0 ? expandedJobId[0] : undefined)
  const proposalsEvents = [...(proposalsQuery.data ?? [])].reverse() as Proposal[]

  const filteredEstimates = activeFilter === 'all'
    ? Array.from(new Set(Object.values(estimates).flat().map(estimate => estimate.projectId)))
      .map(projectId => Object.values(estimates).flat().find(estimate => estimate.projectId === projectId))
    : estimates[activeFilter]

  const getFilterCount = (filterId: string) => {
    if (filterId === 'all') {
      return new Set(Object.values(estimates).flat().map(estimate => estimate.projectId)).size
    }
    return estimates[filterId]?.length || 0
  }

  return <Card
    title={<Space>
      {
        filtersList.map((filter) => <Button
          key={filter.id}
          shape='round'
          type={activeFilter === filter.id ? 'primary' : 'default'}
          onClick={() => setActiveFilter(filter.id)}
        >
          {`${filter.value} (${getFilterCount(filter.id)})`}
        </Button>
        )
      }
    </Space>}
  >
    <div className="estimates-report-table-container">
      <Table
        columns={[
          { title: 'Created Date', dataIndex: 'estimateDateUtc', key: 'estimateDateUtc', render: (date: string) => <Text>{dayjs(date).format('MM/DD/YYYY')}</Text>, sorter: (a, b) => dayjs(a.estimateDateUtc).unix() - dayjs(b.estimateDateUtc).unix(), defaultSortOrder: 'descend' },
          { title: 'Job Number', dataIndex: 'jobNo', key: 'jobNo', sorter: (a, b) => (a.jobNo || '').localeCompare(b.jobNo || '') },
          { title: 'Name', dataIndex: 'jobName', key: 'jobName', sorter: (a, b) => a.jobName.localeCompare(b.jobName) },
          { title: 'Customer', dataIndex: 'displayName', key: 'displayName', sorter: (a, b) => a.displayName.localeCompare(b.displayName) },
          { title: 'Property', dataIndex: 'property', key: 'property', render: (_, record: EstimateReportData) => <Text>{`${record.address1}, ${record.city}, ${record.state}, ${record.zip}`}</Text>, sorter: (a, b) => `${a.address1}, ${a.city}, ${a.state}, ${a.zip}`.localeCompare(`${b.address1}, ${b.city}, ${b.state}, ${b.zip}`) },
          { title: 'Final Price', dataIndex: 'price', key: 'price', render: (value: number) => <Text>{`${centsToDollarsDisplay(value)}`}</Text>, sorter: (a, b) => a.price - b.price },
          { title: 'Job Type', dataIndex: 'jobTypeName', key: 'jobTypeName', render: (_, record: EstimateReportData) => <Text>{projectTypeName(record.jobType)}</Text>, sorter: (a, b) => (a.jobTypeName || '').localeCompare(b.jobTypeName || '') },
          { title: 'Sales Person', dataIndex: 'salesPersonName', key: 'salesPersonName', sorter: (a, b) => a.salesPersonName.localeCompare(b.salesPersonName) },
          {
            title: 'Division', dataIndex: 'divisions', key: 'divisions', render: (_, record: EstimateReportData) =>
              <Text>{record.divisions.split('|')
                .map(id => divisions.find(d => String(d.id) === id)?.name || id)
                .join(', ')}</Text>
          },
          {
            title: 'Status', key: 'status', render: (_, record: EstimateReportData) =>
              <Text>
                <Space>
                  {[
                    estimates.unsent?.some((estimate: EstimateReportData) => estimate.projectId === record.projectId) && estimateStatusIcons[EstimateStatuses.Unsent],
                    estimates.sent?.some((estimate: EstimateReportData) => estimate.projectId === record.projectId) && estimateStatusIcons[EstimateStatuses.Sent],
                    estimates.approved?.some((estimate: EstimateReportData) => estimate.projectId === record.projectId) && estimateStatusIcons[EstimateStatuses.Approved],
                    estimates.lost?.some((estimate: EstimateReportData) => estimate.projectId === record.projectId) && estimateStatusIcons[EstimateStatuses.Lost]
                  ].filter(Boolean)}
                </Space>
              </Text>
          }
        ]}
        dataSource={filteredEstimates}
        rowKey={({ projectId }: EstimateReportData) => projectId}
        onRow={(record: EstimateReportData) => {
          const jobUrl = getProjectUrl(record.projectId, record.jobType, record.isSold, record.jobName)

          return {
            onClick: () => window.location.hash = `#!/${jobUrl}`,
            style: { cursor: 'pointer' }
          }
        }}
        expandable={{
          expandedRowRender: () => proposalsQuery.isLoading
            ? <Skeleton active />
            : <div style={{ paddingLeft: 50 }}>
              {proposalsEvents.length === 0 ? (
                <Text>No proposal history available</Text>
              ) : (
                proposalsEvents?.map((proposal, index) => <div key={proposal.id}>
                  <Text strong>Version {proposalsEvents.length - index}</Text>
                  <ProposalTimeline key={proposal.id} latestProposal={proposal} />
                </div>)
              )}
            </div>,
          onExpand: (_, { projectId }) => {
            if (expandedJobId.includes(projectId)) {
              setExpandedJobId([])
              return
            }

            setExpandedJobId([projectId])
          },
          expandedRowKeys: expandedJobId
        }}
      />
    </div>
  </Card>
}

export default EstimatesReportJobList